import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Sunrise, Sunset } from "lucide-react";
import { taskStatus, STATUS_META } from "@/lib/taskStatus";
import { sunTimesLocal, localOffsetMin } from "@/lib/suntimes";
import { todayLocal } from "@/lib/timefmt";

const hm2min = (t) => { const [h, m] = (t || "0:0").split(":").map(Number); return (h || 0) * 60 + (m || 0); };
const min2hm = (v) => { v = ((Math.round(v) % 1440) + 1440) % 1440; return `${String(Math.floor(v / 60)).padStart(2, "0")}:${String(v % 60).padStart(2, "0")}`; };
const addDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  const p = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const PPH = 56;
const HEADER_H = 26;
const LANE_H = 32;
const BLOCK_H = 22;
const DAY_BG = "rgba(255,255,255,0.94)";
const TWILIGHT_BG = "rgba(203,213,225,0.92)";
const NIGHT_BG = "rgba(100,116,139,0.8)";
const OFFDUTY_BG = "rgba(100,116,139,0.45)";
const GRID_LINE = "rgba(71,85,105,0.35)";

export default function MemberTimelineDialog({ member, onClose }) {
  const [date, setDate] = useState(todayLocal());
  const [data, setData] = useState(null);

  useEffect(() => { if (member) { setDate(todayLocal()); setData(null); } }, [member]);
  useEffect(() => {
    if (!member) return;
    setData(null);
    api.get(`/org/members/${member.id}/task-day?date=${date}`).then((r) => setData(r.data)).catch(() => {});
  }, [member, date]);

  const todayStr = todayLocal();
  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
  const ppm = PPH / 60;
  const width = 24 * PPH;

  const lanes = useMemo(() => {
    const items = (data?.tasks || []).map((t) => {
      let dur = hm2min(t.end) - hm2min(t.start);
      if (dur <= 0) dur += 1440;
      const start = hm2min(t.start);
      return { t, start, dur, brief: t.brief_min || 0, debrief: t.debrief_min || 0, s: start - (t.brief_min || 0), e: start + dur + (t.debrief_min || 0) };
    }).sort((a, b) => a.s - b.s);
    const out = [];
    for (const it of items) {
      const lane = out.find((ln) => ln.every((o) => o.e <= it.s || o.s >= it.e));
      if (lane) lane.push(it); else out.push([it]);
    }
    return out;
  }, [data]);

  if (!member) return null;

  const duty = data?.duty || {};
  const eff = data?.duty_effective || {};
  const loc = data?.location || null;
  const offsetMin = localOffsetMin(loc);
  const sun = loc && loc.lat != null && loc.lng != null ? sunTimesLocal(date, loc.lat, loc.lng, offsetMin) : null;
  const dayOff = data ? (duty.off_work || duty.on_duty === false) : false;
  let ds = duty.start ? hm2min(duty.start) : null;
  let de = duty.end ? hm2min(duty.end) : null;
  if (ds != null && de != null && de <= ds) de += 1440;
  const showDuty = !!data && duty.on_duty && !duty.off_work && ds != null && de != null;
  const height = HEADER_H + Math.max(1, lanes.length) * LANE_H + 22;
  const stripTop = HEADER_H + Math.max(1, lanes.length) * LANE_H;

  return (
    <Dialog open={!!member} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-5xl" data-testid="member-timeline-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-3 flex-wrap">
            {member.first_name} {member.last_name} — day timeline
            {loc && <span className="text-[10px] font-mono-num text-slate-500 font-normal">{loc.name}{loc.dst ? " · DST" : ""}</span>}
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-2 mb-3">
          <button onClick={() => setDate(addDays(date, -1))} data-testid="member-timeline-prev" aria-label="Previous day"
            className="h-8 w-8 grid place-items-center rounded-md bg-slate-950 border border-slate-800 hover:border-slate-600 transition-colors">
            <ChevronLeft className="h-4 w-4" /></button>
          <span className="text-sm font-mono-num text-slate-300" data-testid="member-timeline-date">{date}</span>
          <button onClick={() => setDate(addDays(date, 1))} data-testid="member-timeline-next" aria-label="Next day"
            className="h-8 w-8 grid place-items-center rounded-md bg-slate-950 border border-slate-800 hover:border-slate-600 transition-colors">
            <ChevronRight className="h-4 w-4" /></button>
          {data && showDuty && <span className="text-[11px] font-mono-num text-slate-500 ml-2">Duty {duty.start}–{duty.end}</span>}
          {data && dayOff && <span className="text-[11px] font-mono-num text-slate-500 ml-2">Off duty</span>}
        </div>
        <div className="overflow-x-auto rounded-md border border-slate-800" data-testid="member-timeline-scroll">
          <div className="relative" style={{ width, height, minWidth: "100%" }}>
            <div className="absolute top-0 bottom-0" data-testid="member-timeline-day" style={{ left: 0, width, background: DAY_BG }} />
            {showDuty && (
              <div className="absolute top-0 bottom-0" data-testid="member-timeline-duty"
                style={{ left: ds * ppm, width: (de - ds) * ppm, background: "rgba(59,130,246,0.07)", borderLeft: "1px solid rgba(59,130,246,0.45)", borderRight: "1px solid rgba(59,130,246,0.45)" }} />
            )}
            {showDuty && eff?.start && eff?.end && (() => {
              let es = hm2min(eff.start);
              let ee = hm2min(eff.end);
              if (ee <= es) ee += 1440;
              const segs = [];
              if (es < ds) segs.push([es, ds]);
              if (ee > de) segs.push([de, ee]);
              return segs.map(([a, b], i) => (
                <div key={i} className="absolute top-0 bottom-0" data-testid={`member-timeline-duty-ext-${i}`}
                  style={{ left: a * ppm, width: (b - a) * ppm, background: "rgba(59,130,246,0.07)", borderLeft: "1px dashed rgba(59,130,246,0.6)", borderRight: "1px dashed rgba(59,130,246,0.6)" }} />
              ));
            })()}
            {sun && (() => {
              const nightAmEnd = Math.max(0, Math.min(1440, sun.sunrise - 30));
              const twAmEnd = Math.max(0, Math.min(1440, sun.sunrise));
              const twPmEnd = Math.max(0, Math.min(1440, sun.sunset + 30));
              return (
                <>
                  <div className="absolute top-0 bottom-0" data-testid="member-timeline-night-am"
                    style={{ left: 0, width: nightAmEnd * ppm, background: NIGHT_BG }} />
                  {twAmEnd > nightAmEnd && (
                    <div className="absolute top-0 bottom-0" data-testid="member-timeline-twilight-am"
                      style={{ left: nightAmEnd * ppm, width: (twAmEnd - nightAmEnd) * ppm, background: TWILIGHT_BG }} />
                  )}
                  {twPmEnd > sun.sunset && (
                    <div className="absolute top-0 bottom-0" data-testid="member-timeline-twilight-pm"
                      style={{ left: sun.sunset * ppm, width: (twPmEnd - sun.sunset) * ppm, background: TWILIGHT_BG }} />
                  )}
                  <div className="absolute top-0 bottom-0" data-testid="member-timeline-night-pm"
                    style={{ left: twPmEnd * ppm, width: Math.max(0, 1440 - twPmEnd) * ppm, background: NIGHT_BG, borderLeft: "1px solid rgba(147,197,253,0.35)" }} />
                  <div className="absolute flex items-center gap-1 text-xs font-mono-num font-semibold text-amber-900 bg-amber-200/95 border border-amber-500 rounded px-1.5 py-0.5 shadow" style={{ left: sun.sunrise * ppm - 4, top: stripTop + 1 }}>
                    <Sunrise className="h-3.5 w-3.5" />{min2hm(sun.sunrise)}
                  </div>
                  <div className="absolute flex items-center gap-1 text-xs font-mono-num font-semibold text-orange-900 bg-orange-200/95 border border-orange-500 rounded px-1.5 py-0.5 shadow" style={{ left: sun.sunset * ppm - 4, top: stripTop + 1 }}>
                    <Sunset className="h-3.5 w-3.5" />{min2hm(sun.sunset)}
                  </div>
                </>
              );
            })()}
            {dayOff && (
              <div className="absolute top-0 bottom-0" data-testid="member-timeline-offduty"
                style={{ left: 0, width, background: OFFDUTY_BG }} />
            )}
            {Array.from({ length: 13 }, (_, i) => i * 2).map((h) => (
              <div key={h}>
                <div className="absolute" style={{ left: h * PPH, top: HEADER_H, bottom: 0, borderLeft: `1px dashed ${GRID_LINE}` }} />
                <div className="absolute top-1 text-[9px] font-mono-num text-slate-600" style={{ left: h * PPH + 2 }}>{String(h).padStart(2, "0")}</div>
              </div>
            ))}
            {lanes.map((lane, li) => (
              <div key={li} className="absolute left-0 right-0" style={{ top: HEADER_H + li * LANE_H, height: LANE_H }}>
                {lane.map((it) => {
                  const st = taskStatus(it.t, null, todayStr, nowMin);
                  const meta = STATUS_META[st];
                  const wB = it.brief * ppm;
                  const wT = it.dur * ppm;
                  const wD = it.debrief * ppm;
                  return (
                    <div key={it.t.id} className="absolute" data-testid={`member-timeline-task-${it.t.id}`}
                      style={{ left: it.s * ppm, width: wB + wT + wD, top: 5, height: BLOCK_H }}
                      title={`${it.t.name || it.t.task_type_name} ${it.t.start}–${it.t.end} · brief ${it.brief}m · debrief ${it.debrief}m · ${meta.label}`}>
                      {it.brief > 0 && (
                        <div className="absolute top-0 bottom-0 rounded-l-sm"
                          style={{ left: 0, width: wB, background: "repeating-linear-gradient(45deg, rgba(148,163,184,0.35) 0 4px, rgba(148,163,184,0.15) 4px 8px)" }} />
                      )}
                      <div className="absolute top-0 bottom-0 flex items-center justify-center overflow-hidden text-[10px] font-medium text-white select-none"
                        style={{ left: wB, width: wT, background: meta.block, borderRadius: 2, borderLeft: it.t.color ? `3px solid ${it.t.color}` : undefined }}>
                        {wT > 60 && <span className={`truncate px-1 ${st === "cancelled" ? "line-through" : ""}`}>{it.t.name || it.t.task_type_name} {it.t.start}–{it.t.end}</span>}
                      </div>
                      {it.debrief > 0 && (
                        <div className="absolute top-0 bottom-0 rounded-r-sm"
                          style={{ left: wB + wT, width: wD, background: "repeating-linear-gradient(45deg, rgba(148,163,184,0.35) 0 4px, rgba(148,163,184,0.15) 4px 8px)" }} />
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
            {data && lanes.length === 0 && (
              <div className="absolute text-xs text-slate-600" style={{ top: HEADER_H + 12, left: 12 }} data-testid="member-timeline-empty">No tasks this day.</div>
            )}
            {!data && (
              <div className="absolute text-xs text-slate-600" style={{ top: HEADER_H + 12, left: 12 }}>Loading…</div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 mt-2 text-[10px] font-mono-num text-slate-500 flex-wrap">
          {Object.entries(STATUS_META).map(([k, m]) => (
            <span key={k} className="inline-flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-4 rounded-sm" style={{ background: m.block }} /> {m.label}
            </span>
          ))}
          <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-4 border border-slate-600" style={{ background: DAY_BG }} /> Day</span>
          <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-4" style={{ background: TWILIGHT_BG }} /> Twilight</span>
          <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-4" style={{ background: NIGHT_BG }} /> Night</span>
          <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-4" style={{ background: OFFDUTY_BG }} /> Off duty</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
